import { useQuery } from '@tanstack/react-query';
import { Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { fetchSkutkiUsuniecia, usunGrupe, type SkutkiUsuniecia } from '@/lib/employee-groups-api';
import { useI18n } from '@/lib/i18n';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  USUNIĘCIE GRUPY PRACOWNIKÓW — okno Z TREŚCIĄ (13.10.2026, user: „wszystko wyświetlać w okienku
 *  dialogowym usuwania").
 *
 *  ⚠️ NAJPIERW BLOKADY, POTEM SKUTKI. Blokady (szuflady/klapy/pozycje automatu, harmonogramy raportów,
 *  zmiany w kolejce) wyłączają przycisk — decyzje usera 13.10. Skutki to wszystko, co poleci kaskadą
 *  po tabelach SPS (`usuniecie-grupy.ts` w API), z liczbą osób, które stracą dostęp do automatów.
 *
 *  ⚠️ POTWIERDZENIE PRZEPISANYM KODEM GRUPY — kaskada rusza setki wierszy w bazie klienta (Gestamp:
 *  359 członkostw jednej grupy), więc jedno kliknięcie nie może jej odpalić.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function UsuniecieGrupyPracownikow({
  grupaId,
  islandId,
  onClose,
  onSaved,
}: {
  grupaId: string;
  islandId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [kod, setKod] = useState('');
  const [busy, setBusy] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const skutki = useQuery({
    queryKey: ['employee-groups', 'delete-impact', grupaId, islandId],
    queryFn: () => fetchSkutkiUsuniecia(grupaId, islandId),
  });
  const d = skutki.data;

  const usun = async () => {
    setBlad(null);
    setBusy(true);
    try {
      await usunGrupe(grupaId, islandId);
      toast.success(t('grp.usun.zlecone'));
      onSaved();
      onClose();
    } catch (e) {
      setBlad(e instanceof ApiError ? e.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('grp.usun.tytul')} description={d ? `${d.grupa.nazwa} (${d.grupa.kod})` : undefined}>
        {skutki.isPending || !d ? (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
          </p>
        ) : (
          <TrescUsuniecia d={d} kod={kod} setKod={setKod} blad={blad} />
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!d || !d.wolnoUsunac || kod.trim() !== d.grupa.kod || busy}
            onClick={() => void usun()}
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
            {t('grp.usun.przycisk')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const nazwaAutomatu = (a: { vmid: number; automat: string | null }) => a.automat ?? `VMID ${a.vmid}`;

/** Treść okna — osobno, żeby dało się ją narysować w teście bez zapytań. */
export function TrescUsuniecia({
  d,
  kod,
  setKod,
  blad,
}: {
  d: SkutkiUsuniecia;
  kod: string;
  setKod: (s: string) => void;
  blad: string | null;
}) {
  const { t } = useI18n();
  const b = d.blokady;
  const blokady = [
    ...b.szuflady.map((s) => t('grp.usun.blokSzuflada').replace('{automat}', nazwaAutomatu(s)).replace('{beben}', String(s.beben)).replace('{szuflada}', String(s.szuflada))),
    ...b.klapy.map((k) =>
      t('grp.usun.blokKlapa').replace('{automat}', nazwaAutomatu(k)).replace('{szuflada}', String(k.szuflada)).replace('{klapa}', String(k.klapa)),
    ),
    ...b.pozycje.map((p) => t('grp.usun.blokPozycja').replace('{automat}', nazwaAutomatu(p)).replace('{towar}', p.towar).replace('{role}', p.role.join(', '))),
    ...b.raporty.map((r) => t('grp.usun.blokRaport').replace('{nazwa}', r.nazwa)),
    ...(b.wKolejce ? [t('grp.usun.blokKolejka')] : []),
  ];

  return (
    <div className="space-y-3 text-sm">
      {blokady.length > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-xs text-destructive">
          <p className="font-medium">{t('grp.usun.blokady')}</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {blokady.map((z) => (
              <li key={z}>{z}</li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <p className="font-medium">{t('grp.usun.zniknie')}</p>
        <ul className="mt-1 space-y-1">
          <li>
            <span className="font-semibold tabular-nums">{d.czlonkowie.liczba}</span> {t('grp.usun.czlonkostwa')}
            {d.czlonkowie.bezInnejGrupy > 0 && (
              <span className="text-[var(--warning)]">
                {' '}
                · {t('grp.usun.bezInnejGrupy').replace('{n}', String(d.czlonkowie.bezInnejGrupy))}
              </span>
            )}
            {d.czlonkowie.probka.length > 0 && (
              <details className="mt-0.5 text-xs text-muted-foreground">
                <summary className="cursor-pointer">{t('grp.usun.pokazOsoby')}</summary>
                <p className="mt-1">
                  {d.czlonkowie.probka.map((o) => `${o.nazwa} (${o.numer})`).join(', ')}
                  {d.czlonkowie.liczba > d.czlonkowie.probka.length && ` … +${d.czlonkowie.liczba - d.czlonkowie.probka.length}`}
                </p>
              </details>
            )}
          </li>
          <li>
            <span className="font-semibold tabular-nums">{d.automaty.length}</span> {t('grp.usun.automaty')}
            {d.automaty.length > 0 && <span className="text-xs text-muted-foreground"> ({d.automaty.map(nazwaAutomatu).join(', ')})</span>}
          </li>
          {d.dostep.tracaAutomat > 0 && (
            <li className="text-[var(--warning)]">
              {t('grp.usun.tracaDostep').replace('{n}', String(d.dostep.tracaAutomat))}
              {d.dostep.bezZadnegoAutomatu > 0 && ` · ${t('grp.usun.bezAutomatu').replace('{n}', String(d.dostep.bezZadnegoAutomatu))}`}
            </li>
          )}
          <li>
            <span className="font-semibold tabular-nums">{d.operacje.length}</span> {t('grp.usun.operacje')}
            {d.operacje.length > 0 && <span className="font-mono text-xs text-muted-foreground"> {d.operacje.join(', ')}</span>}
          </li>
          <li className={d.limity.length > 0 ? 'text-[var(--warning)]' : undefined}>
            <span className="font-semibold tabular-nums">{d.limity.length}</span> {t('grp.usun.limity')}
            {d.limity.length > 0 && (
              <span className="text-xs text-muted-foreground"> ({d.limity.map((l) => `${l.itemKey}: ${l.maxQty}`).join(', ')})</span>
            )}
          </li>
          <li className={d.uprawnienia.length > 0 ? 'text-[var(--warning)]' : undefined}>
            <span className="font-semibold tabular-nums">{d.uprawnienia.length}</span> {t('grp.usun.uprawnienia')}
            {d.uprawnienia.length > 0 && (
              <span className="text-xs text-muted-foreground"> ({d.uprawnienia.map((u) => u.rItem).join(', ')})</span>
            )}
          </li>
        </ul>
        <p className="mt-1 text-xs text-muted-foreground">{t('grp.usun.historia')}</p>
      </div>

      {d.hrPlus.wlaczona && (d.hrPlus.czlonkowZHr > 0 || d.hrPlus.wWyjatkach) && (
        <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2.5 text-xs">
          {t('grp.usun.hrPlus').replace('{n}', String(d.hrPlus.czlonkowZHr))}
          {d.hrPlus.wWyjatkach && ` ${t('grp.usun.hrPlusWyjatek')}`}
        </p>
      )}

      {d.wolnoUsunac && (
        <label className="flex flex-col gap-1 text-xs">
          <span>{t('grp.usun.potwierdz').replace('{kod}', d.grupa.kod)}</span>
          <Input className="h-8 font-mono" autoComplete="off" value={kod} onChange={(e) => setKod(e.target.value)} />
        </label>
      )}

      {blad !== null && <p className="text-xs text-destructive">{blad}</p>}
    </div>
  );
}
